import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, FC } from 'react';

import { FormattedMessage } from 'react-intl';

import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import { fetchCommunity, leaveCommunity } from 'mastodon/actions/communities';
import { createRoom } from 'mastodon/actions/rooms';
import { me } from 'mastodon/initial_state';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

import { canAccessManage, canStartRoomAsAdmin, viewerRank } from '../roles';

import { CommunityJoinControls } from './community_join_controls';

/**
 * Sidebar panel that appears in the left ComposePanel when the user is
 * viewing a community. Holds all community-specific controls (join/leave,
 * manage, etc.) and the rules list — keeping the main timeline column
 * focused on posts.
 *
 * Hidden on mobile (the parent ComposePanel is hidden in single-column
 * layouts); the community show page surfaces an "About" modal there.
 */
export const CommunityContextPanel: FC = () => {
  const dispatch = useAppDispatch();
  const match = useRouteMatch<{ slug: string }>({
    path: '/communities/:slug',
    exact: false,
  });
  const slug = match?.params.slug;

  // Skip the panel on /communities/new and similar non-show pages.
  const isShowPage =
    !!slug && slug !== 'new' && match.path === '/communities/:slug';

  const community = useAppSelector((s) =>
    isShowPage
      ? Object.values(s.communities.byId).find((c) => c.slug === slug)
      : undefined,
  );

  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (isShowPage && slug) {
      void dispatch(fetchCommunity(slug)).catch(() => {
        // The show column owns the error/invite UI; nothing to do here.
      });
    }
  }, [dispatch, slug, isShowPage]);

  const communityId = community?.id;

  const handleLeave = useCallback(() => {
    if (!communityId) return;
    setBusy(true);
    setActionError(null);
    dispatch(leaveCommunity(communityId))
      .catch((e: unknown) => {
        const err = e as { response?: { data?: { error?: string } } };
        setActionError(err.response?.data?.error ?? 'Failed to leave');
      })
      .finally(() => {
        setBusy(false);
      });
  }, [dispatch, communityId]);

  if (!isShowPage || !community) return null;

  const isMember = community.viewer_state === 'member';
  const myRank = viewerRank(community, me);

  return (
    <aside className='community-context-panel'>
      <header className='community-context-panel__header'>
        <h3 className='community-context-panel__title'>{community.name}</h3>
        <div className='community-context-panel__handle'>@{community.slug}</div>
        {community.description && (
          <p className='community-context-panel__description'>
            {community.description}
          </p>
        )}
        <div className='community-context-panel__meta'>
          <FormattedMessage
            id='communities.show.members'
            defaultMessage='{count, plural, one {# member} other {# members}}'
            values={{ count: community.members_count }}
          />
          <span aria-hidden='true'> · </span>
          {community.privacy === 'private' ? (
            <FormattedMessage
              id='communities.card.private'
              defaultMessage='Private'
            />
          ) : (
            <FormattedMessage
              id='communities.card.public'
              defaultMessage='Public'
            />
          )}
        </div>
      </header>

      {actionError && (
        <div className='community-context-panel__error'>{actionError}</div>
      )}

      <div className='community-context-panel__actions'>
        {!isMember && <CommunityJoinControls community={community} />}

        {isMember && (
          <button
            type='button'
            className='button button-tertiary'
            onClick={handleLeave}
            disabled={busy}
          >
            <FormattedMessage id='communities.leave' defaultMessage='Leave' />
          </button>
        )}

        {canAccessManage(myRank) && (
          <Link
            to={`/communities/${community.slug}/manage`}
            className='button button-tertiary'
          >
            <FormattedMessage id='communities.manage' defaultMessage='Manage' />
            {!!community.pending_join_requests_count &&
              community.pending_join_requests_count > 0 && (
                <span className='community-context-panel__pending-badge'>
                  {community.pending_join_requests_count}
                </span>
              )}
          </Link>
        )}

        {canStartRoomAsAdmin(myRank) && (
          <StartCommunityRoomButton
            communityId={community.id}
            communityName={community.name}
          />
        )}
      </div>

      {community.rules.length > 0 && (
        <section className='community-context-panel__rules'>
          <h4>
            <FormattedMessage id='communities.rules' defaultMessage='Rules' />
          </h4>
          <ol>
            {community.rules.map((rule) => (
              <li key={rule.id}>{rule.text}</li>
            ))}
          </ol>
        </section>
      )}
    </aside>
  );
};

// "Start room as admin" — admins and the creator only; mods do not get it.
// The server refuses a community room to anyone below admin as well.
// Click → expands an inline title input → submit → POST /v1/rooms with
// community_id → server fans out a community_room_started notification to
// every other member, then we navigate the host into the room.
const StartCommunityRoomButton: FC<{
  communityId: string;
  communityName: string;
}> = ({ communityId, communityName }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleToggle = useCallback(() => {
    setOpen((v) => !v);
    setError(null);
    if (!title) setTitle(`${communityName} — live audio`);
  }, [title, communityName]);

  const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  }, []);

  const handleStart = useCallback(async () => {
    if (!title.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const room = await dispatch(
        createRoom(title.trim(), undefined, communityId),
      );
      history.push(`/rooms/${room.id}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Could not start room');
      setBusy(false);
    }
  }, [dispatch, title, communityId, history]);

  return (
    <div className='community-context-panel__start-room'>
      <button
        type='button'
        className='button button-tertiary'
        onClick={handleToggle}
        disabled={busy}
      >
        <FormattedMessage
          id='communities.start_room_as_admin'
          defaultMessage='Start room as admin'
        />
      </button>
      {open && (
        <div className='community-context-panel__start-room__form'>
          <input
            type='text'
            value={title}
            onChange={handleChange}
            maxLength={120}
            placeholder='Room title'
          />
          <button
            type='button'
            className='button'
            onClick={handleStart}
            disabled={busy || !title.trim()}
          >
            <FormattedMessage
              id='communities.start_room'
              defaultMessage='Start'
            />
          </button>
          {error && (
            <div className='community-context-panel__error'>{error}</div>
          )}
        </div>
      )}
    </div>
  );
};
